import React, { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: "Bearer " + token() });
const NAVY = "#1A3A5C";
const ORANGE = "#EA6F2E";
const GOLD = "#F59E0B";

const DEPARTMENTS = [
  "Administration","Collections","Education","Finance",
  "Marketing","Operations","Public Programs","Security","Other"
];

const COMPUTER_TYPES = ["Desktop", "Laptop", "iMac", "Mac Mini", "Other"];
const SOFTWARE_EXTRAS = ["Fi$Cal", "CalAters", "Adobe Cloud Suite"];

const BTN = ({ onClick, children, variant="default", style={}, disabled=false }) => {
  const base = { display:"inline-flex", alignItems:"center", gap:"6px", padding:"8px 18px", borderRadius:"7px", fontSize:"13px", fontWeight:"600", cursor: disabled ? "not-allowed" : "pointer", border:"1px solid", opacity: disabled ? 0.5 : 1, transition:"all 0.15s" };
  const variants = {
    default: { background:"#fff", borderColor:"#E5E7EB", color:"#374151" },
    primary: { background: NAVY, borderColor: NAVY, color:"#fff" },
    orange:  { background: ORANGE, borderColor: ORANGE, color:"#fff" },
    gold:    { background: GOLD, borderColor: GOLD, color:"#fff" },
    danger:  { background:"#EF4444", borderColor:"#EF4444", color:"#fff" },
  };
  return <button onClick={onClick} disabled={disabled} style={{...base, ...variants[variant], ...style}}>{children}</button>;
};

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 5000); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position:"fixed", bottom:"32px", left:"50%", transform:"translateX(-50%)", zIndex:9999, background:"#111827", color:"#fff", padding:"13px 24px", borderRadius:"10px", boxShadow:"0 8px 32px rgba(0,0,0,0.3)", fontSize:"13px", fontWeight:"500", display:"flex", alignItems:"center", gap:"10px", maxWidth:"500px" }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12"/></svg>
      {message}
    </div>
  );
}

function Confetti() {
  const [pieces] = useState(() => 
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: ["#F59E0B", "#EA6F2E", "#10B981", "#3B82F6", "#8B5CF6"][Math.floor(Math.random() * 5)]
    }))
  );

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, pointerEvents:"none", zIndex:10000 }}>
      {pieces.map(p => (
        <div
          key={p.id}
          style={{
            position:"absolute",
            left: p.left + "%",
            top:"-10px",
            width:"10px",
            height:"10px",
            background: p.color,
            opacity:0.8,
            animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
            transform: `rotate(${Math.random() * 360}deg)`
          }}
        />
      ))}
      <style>{`
        @keyframes confettiFall {
          to { transform: translateY(100vh) rotate(${Math.random() * 720}deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// Congratulations Letter Modal
function CongratulationsLetterModal({ job, candidate, onClose, onSent }) {
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  const [letterContent, setLetterContent] = useState("");

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const generateLetter = () => {
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    return `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 650px; margin: 0 auto; padding: 40px 20px; color: #111827;">
        <div style="text-align: center; margin-bottom: 40px;">
          <img src="/caam-logo.png" alt="California African American Museum" style="height: 80px; margin-bottom: 16px;" />
          <div style="font-size: 11px; color: #6B7280; letter-spacing: 0.5px;">
            600 State Drive, Exposition Park, Los Angeles, CA 90037<br/>
            (213) 744-7432 • www.caamuseum.org
          </div>
        </div>

        <div style="margin-bottom: 32px; font-size: 13px; color: #6B7280;">
          ${today}
        </div>

        <div style="margin-bottom: 32px; font-size: 14px; line-height: 1.6;">
          <strong>${candidate.first_name} ${candidate.last_name}</strong><br/>
          ${candidate.personal_email || ''}
        </div>

        <div style="margin-bottom: 24px;">
          <strong style="font-size: 16px; color: ${NAVY};">RE: Congratulations on Your New Position</strong>
        </div>

        <div style="font-size: 14px; line-height: 1.8; color: #374151;">
          <p style="margin-bottom: 16px;">Dear ${candidate.first_name},</p>

          <p style="margin-bottom: 16px;">
            On behalf of the California African American Museum, I am delighted to inform you that you have been selected for the position of <strong>${job.job_title}</strong>${job.department ? ` in our ${job.department} department` : ''}. Your skills, experience, and enthusiasm made you the ideal candidate for this role.
          </p>

          <p style="margin-bottom: 16px;">
            We are thrilled to welcome you to our team. The California African American Museum is dedicated to researching, collecting, preserving, and interpreting the history, art, and culture of African Americans, with a particular emphasis on California and the western United States. Your contributions will help us continue this vital mission.
          </p>

          <p style="margin-bottom: 16px;">
            <strong>Position Details:</strong><br/>
            <span style="display: inline-block; margin-left: 20px;">
              • <strong>Title:</strong> ${job.job_title}<br/>
              ${job.department ? `• <strong>Department:</strong> ${job.department}<br/>` : ''}
              ${job.supervisor ? `• <strong>Supervisor:</strong> ${job.supervisor}<br/>` : ''}
              ${job.office_location ? `• <strong>Office:</strong> ${job.office_location}<br/>` : ''}
              • <strong>Start Date:</strong> ${formatDate(job.target_start_date)}
            </span>
          </p>

          <p style="margin-bottom: 16px;">
            Our IT and HR teams will be reaching out to you shortly to coordinate your onboarding process, including equipment setup, workspace preparation, and orientation scheduling. Please don't hesitate to reach out if you have any questions in the meantime.
          </p>

          <p style="margin-bottom: 16px;">
            We look forward to the contributions you will make to our organization and to working alongside you as we advance our mission together.
          </p>

          <p style="margin-bottom: 32px;">
            Welcome to the California African American Museum family!
          </p>

          <p style="margin-bottom: 8px;">Warmest regards,</p>
          
          <div style="margin-top: 40px;">
            <strong style="display: block; margin-bottom: 4px;">Jon Lamirault</strong>
            <span style="color: #6B7280; font-size: 13px;">Director of Administration</span><br/>
            <span style="color: #6B7280; font-size: 13px;">California African American Museum</span>
          </div>
        </div>
      </div>
    `;
  };

  useEffect(() => {
    setLetterContent(generateLetter());
  }, []);

  const handleSend = async () => {
    if (!candidate.personal_email) {
      alert("Candidate email is missing");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(API + "/email/send-congratulations", {
        job_id: job.id,
        recipient_email: candidate.personal_email,
        subject: `Congratulations - ${job.job_title} Position at California African American Museum`,
        html_content: letterContent
      }, { headers: headers() });
      
      onSent();
      onClose();
    } catch (e) {
      alert("Failed to send: " + (e.response?.data?.error || e.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", overflowY:"auto", padding:"20px" }}>
      <div style={{ background:"#fff", borderRadius:"14px", width:"900px", maxWidth:"95vw", maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 64px rgba(0,0,0,0.3)" }}>
        
        {/* Header */}
        <div style={{ padding:"24px 32px", borderBottom:"1px solid #E5E7EB", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <h2 style={{ fontSize:"18px", fontWeight:"700", color:"#111827", margin:0 }}>Congratulations Letter</h2>
            <p style={{ fontSize:"12px", color:"#9CA3AF", margin:"4px 0 0 0" }}>
              {candidate.first_name} {candidate.last_name} • {job.job_title}
            </p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", fontSize:"22px", lineHeight:1 }}>×</button>
        </div>

        {/* Letter Content */}
        <div style={{ flex:1, overflowY:"auto", padding:"32px" }}>
          {editing ? (
            <div
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => setLetterContent(e.currentTarget.innerHTML)}
              dangerouslySetInnerHTML={{ __html: letterContent }}
              style={{
                minHeight:"500px",
                padding:"20px",
                border:"2px solid #E5E7EB",
                borderRadius:"8px",
                outline:"none",
                background:"#FAFAFA"
              }}
            />
          ) : (
            <div dangerouslySetInnerHTML={{ __html: letterContent }} />
          )}
        </div>

        {/* Footer Actions */}
        <div style={{ padding:"20px 32px", borderTop:"1px solid #E5E7EB", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#F9FAFB" }}>
          <div>
            {!editing && (
              <BTN variant="default" onClick={() => setEditing(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit Letter
              </BTN>
            )}
            {editing && (
              <BTN variant="primary" onClick={() => setEditing(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Save Changes
              </BTN>
            )}
          </div>
          <div style={{ display:"flex", gap:"8px" }}>
            <BTN onClick={onClose}>Cancel</BTN>
            {!editing && (
              <>
                <BTN variant="default" disabled>Schedule Send</BTN>
                <BTN variant="orange" onClick={handleSend} disabled={sending}>
                  {sending ? "Sending..." : "Send Now"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </BTN>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// PHASE 1: Job Details & Equipment
function JobDetailsWizard({ onClose, onSave, onAdvanceToCandidates }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [offices, setOffices] = useState([]);
  const [sharedDrives, setSharedDrives] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadingGoogle, setLoadingGoogle] = useState(true);
  const [supervisors, setSupervisors] = useState([]);
  const [vacantCards, setVacantCards] = useState([]);

  const [form, setForm] = useState({
    job_title: "", department: "", job_classification: "", job_designation: "",
    hiring_manager: "", supervisor: "", target_start_date: "", notes: "", office_location: "",
    org_chart_position_id: "",
    computer_types: [], software_standard: false, software_extras: [],
    google_shared_drives: [], google_calendars: [], google_groups: []
  });

  useEffect(() => {
    fetchOffices();
    fetchGoogleData();
    fetchSupervisors();
    fetchVacantCards();
  }, []);

  // Existing vacant org-chart cards not yet tied to an opening — for the backfill picker.
  const fetchVacantCards = async () => {
    try {
      const res = await axios.get(API + "/preonboarding/vacant-positions", { headers: headers() });
      setVacantCards(res.data || []);
    } catch (e) { console.error("Failed to fetch vacant positions:", e); }
  };

  const fetchOffices = async () => {
    try {
      const res = await axios.get(API + "/preonboarding/offices", { headers: headers() });
      setOffices(res.data);
    } catch (e) { console.error("Failed to fetch offices:", e); }
  };

  // Only designated supervisors are selectable (staff.is_supervisor).
  const fetchSupervisors = async () => {
    try {
      const res = await axios.get(API + "/staff", { headers: headers() });
      setSupervisors((res.data || []).filter(s => s.is_supervisor));
    } catch (e) { console.error("Failed to fetch supervisors:", e); }
  };

  const fetchGoogleData = async () => {
    setLoadingGoogle(true);
    try {
      const [drivesRes, calsRes, groupsRes] = await Promise.all([
        axios.get(API + "/google/shared-drives", { headers: headers() }).catch(() => ({ data: [] })),
        axios.get(API + "/google/calendars", { headers: headers() }).catch(() => ({ data: [] })),
        axios.get(API + "/google/groups", { headers: headers() }).catch(() => ({ data: [] }))
      ]);
      setSharedDrives(drivesRes.data || []);
      setCalendars(calsRes.data || []);
      setGroups(groupsRes.data || []);
    } catch (e) { console.error("Failed to fetch Google data:", e); }
    finally { setLoadingGoogle(false); }
  };

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  
  const toggleCheckbox = (key, value) => {
    setForm(f => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter(x => x !== value) : [...f[key], value]
    }));
  };

  const toggleGoogleItem = (key, item) => {
    const idKey = key === 'google_groups' ? 'email' : 'id';
    const exists = form[key].find(x => x[idKey] === item[idKey]);
    setForm(f => ({
      ...f,
      [key]: exists ? f[key].filter(x => x[idKey] !== item[idKey]) : [...f[key], item]
    }));
  };

  const handleSave = async () => {
    if (!form.job_title.trim()) { setError("Job title is required."); return; }
    setSaving(true); setError("");
    try {
      await axios.post(API + "/preonboarding", form, { headers: headers() });
      onSave();
    } catch (e) { setError(e.response?.data?.error || "Failed to save job."); }
    finally { setSaving(false); }
  };

  const handleAdvance = async () => {
    if (!form.job_title.trim()) { setError("Job title is required."); return; }
    setSaving(true); setError("");
    try {
      const res = await axios.post(API + "/preonboarding", form, { headers: headers() });
      onAdvanceToCandidates(res.data.id);
    } catch (e) { setError(e.response?.data?.error || "Failed to create job."); }
    finally { setSaving(false); }
  };

  const inputStyle = { width:"100%", padding:"9px 11px", border:"1px solid #E5E7EB", borderRadius:"6px", fontSize:"13px", color:"#111827", outline:"none", boxSizing:"border-box" };
  const labelStyle = { display:"block", fontSize:"11px", fontWeight:"600", color:"#6B7280", marginBottom:"5px", textTransform:"uppercase", letterSpacing:"0.04em" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", overflowY:"auto", padding:"20px" }}>
      <div style={{ background:"#fff", borderRadius:"14px", padding:"36px", width:"900px", maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"28px" }}>
          <div>
            <h2 style={{ fontSize:"18px", fontWeight:"700", color:"#111827", margin:0 }}>New Job Opening</h2>
            <p style={{ fontSize:"12px", color:"#9CA3AF", margin:"4px 0 0 0" }}>Phase 1: Job Details & Equipment</p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", fontSize:"22px", lineHeight:1 }}>×</button>
        </div>

        {error && <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:"6px", padding:"10px 14px", marginBottom:"18px", fontSize:"12px", color:"#B91C1C" }}>{error}</div>}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"24px" }}>
          <div style={{ gridColumn:"1 / -1" }}>
            <label style={labelStyle}>Job Title *</label>
            <input value={form.job_title} onChange={e => set("job_title", e.target.value)} style={inputStyle} placeholder="e.g. Collections Manager"/>
          </div>
          <div>
            <label style={labelStyle}>Department</label>
            <select value={form.department} onChange={e => set("department", e.target.value)} style={inputStyle}>
              <option value="">Select department...</option>
              {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Hiring Manager</label>
            <input value={form.hiring_manager} onChange={e => set("hiring_manager", e.target.value)} style={inputStyle} placeholder="e.g. Jon Lamirault"/>
          </div>
          <div>
            <label style={labelStyle}>Supervisor</label>
            <select value={form.supervisor} onChange={e => set("supervisor", e.target.value)} style={inputStyle}>
              <option value="">Select supervisor...</option>
              {supervisors.map(s => {
                const name = `${s.first_name} ${s.last_name}`;
                return <option key={s.id} value={name}>{name}</option>;
              })}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Job Classification</label>
            <input value={form.job_classification} onChange={e => set("job_classification", e.target.value)} style={inputStyle} placeholder="e.g. Exempt, Represented"/>
          </div>
          <div>
            <label style={labelStyle}>Job Designation</label>
            <input value={form.job_designation} onChange={e => set("job_designation", e.target.value)} style={inputStyle} placeholder="e.g. Staff Services Analyst"/>
          </div>
          <div>
            <label style={labelStyle}>Office Location</label>
            <select value={form.office_location} onChange={e => set("office_location", e.target.value)} style={inputStyle}>
              <option value="">Select office...</option>
              {offices.map(o => <option key={o.id} value={o.office_number}>{o.office_number} - {o.office_name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Target Start Date</label>
            <input type="date" value={form.target_start_date} onChange={e => set("target_start_date", e.target.value)} style={inputStyle}/>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <label style={labelStyle}>Org Chart Placement</label>
            <select value={form.org_chart_position_id} onChange={e => set("org_chart_position_id", e.target.value)} style={inputStyle}>
              <option value="">Create a new vacancy card on the org chart (default)</option>
              {vacantCards.map(c => {
                const sup = (c.supervisor_first || c.supervisor_last) ? ` — ${`${c.supervisor_first || ''} ${c.supervisor_last || ''}`.trim()}` : '';
                const dept = c.department ? ` (${c.department})` : '';
                return <option key={c.id} value={c.id}>{`Link to existing vacancy: ${c.position_title}${dept}${sup}`}</option>;
              })}
            </select>
            <div style={{ fontSize:"11px", color:"#9CA3AF", marginTop:"5px" }}>
              For a backfill, link to the vacant card already on the chart so a duplicate isn't created.
            </div>
          </div>
        </div>

        <div style={{ marginBottom:"20px" }}>
          <label style={labelStyle}>Computer Type(s)</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"12px" }}>
            {COMPUTER_TYPES.map(type => (
              <label key={type} style={{ display:"flex", alignItems:"center", gap:"6px", cursor:"pointer" }}>
                <input 
                  type="checkbox" 
                  checked={form.computer_types.includes(type)}
                  onChange={() => toggleCheckbox("computer_types", type)}
                  style={{ width:"16px", height:"16px", cursor:"pointer" }}
                />
                <span style={{ fontSize:"13px", color:"#374151" }}>{type}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:"20px" }}>
          <label style={{ ...labelStyle, display:"flex", alignItems:"center", gap:"8px", cursor:"pointer" }}>
            <input 
              type="checkbox" 
              checked={form.software_standard}
              onChange={e => set("software_standard", e.target.checked)}
              style={{ width:"16px", height:"16px", cursor:"pointer" }}
            />
            <span>Standard Software (Google Workspace, Microsoft O365, Zoom, Docusign, Asana, Adobe Acrobat)</span>
          </label>
        </div>

        <div style={{ marginBottom:"20px" }}>
          <label style={labelStyle}>Software Extras</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"12px" }}>
            {SOFTWARE_EXTRAS.map(extra => (
              <label key={extra} style={{ display:"flex", alignItems:"center", gap:"6px", cursor:"pointer" }}>
                <input 
                  type="checkbox" 
                  checked={form.software_extras.includes(extra)}
                  onChange={() => toggleCheckbox("software_extras", extra)}
                  style={{ width:"16px", height:"16px", cursor:"pointer" }}
                />
                <span style={{ fontSize:"13px", color:"#374151" }}>{extra}</span>
              </label>
            ))}
          </div>
        </div>

        {loadingGoogle ? (
          <div style={{ textAlign:"center", padding:"20px", color:"#9CA3AF", fontSize:"13px" }}>Loading Google data...</div>
        ) : (
          <>
            {sharedDrives.length > 0 && (
              <div style={{ marginBottom:"20px" }}>
                <label style={labelStyle}>Google Shared Drives</label>
                <div style={{ display:"flex", flexDirection:"column", gap:"8px", maxHeight:"120px", overflowY:"auto", border:"1px solid #E5E7EB", borderRadius:"6px", padding:"10px" }}>
                  {sharedDrives.map(drive => (
                    <label key={drive.id} style={{ display:"flex", alignItems:"center", gap:"6px", cursor:"pointer" }}>
                      <input 
                        type="checkbox" 
                        checked={form.google_shared_drives.some(d => d.id === drive.id)}
                        onChange={() => toggleGoogleItem("google_shared_drives", drive)}
                        style={{ width:"16px", height:"16px", cursor:"pointer" }}
                      />
                      <span style={{ fontSize:"13px", color:"#374151" }}>{drive.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {calendars.length > 0 && (
              <div style={{ marginBottom:"20px" }}>
                <label style={labelStyle}>Google Calendars</label>
                <div style={{ display:"flex", flexDirection:"column", gap:"8px", maxHeight:"120px", overflowY:"auto", border:"1px solid #E5E7EB", borderRadius:"6px", padding:"10px" }}>
                  {calendars.map(cal => (
                    <label key={cal.id} style={{ display:"flex", alignItems:"center", gap:"6px", cursor:"pointer" }}>
                      <input 
                        type="checkbox" 
                        checked={form.google_calendars.some(c => c.id === cal.id)}
                        onChange={() => toggleGoogleItem("google_calendars", cal)}
                        style={{ width:"16px", height:"16px", cursor:"pointer" }}
                      />
                      <span style={{ fontSize:"13px", color:"#374151" }}>{cal.summary}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {groups.length > 0 && (
              <div style={{ marginBottom:"20px" }}>
                <label style={labelStyle}>Google Groups</label>
                <div style={{ display:"flex", flexDirection:"column", gap:"8px", maxHeight:"120px", overflowY:"auto", border:"1px solid #E5E7EB", borderRadius:"6px", padding:"10px" }}>
                  {groups.map(group => (
                    <label key={group.email} style={{ display:"flex", alignItems:"center", gap:"6px", cursor:"pointer" }}>
                      <input 
                        type="checkbox" 
                        checked={form.google_groups.some(g => g.email === group.email)}
                        onChange={() => toggleGoogleItem("google_groups", group)}
                        style={{ width:"16px", height:"16px", cursor:"pointer" }}
                      />
                      <span style={{ fontSize:"13px", color:"#374151" }}>{group.name} ({group.email})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ marginBottom:"20px" }}>
          <label style={labelStyle}>Notes</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} style={{...inputStyle, minHeight:"80px", resize:"vertical"}} placeholder="Additional notes about this position..."/>
        </div>

        <div style={{ display:"flex", justifyContent:"flex-end", gap:"8px" }}>
          <BTN onClick={onClose}>Cancel</BTN>
          <BTN variant="default" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</BTN>
          <BTN variant="orange" onClick={handleAdvance} disabled={saving}>{saving ? "Saving..." : "Award Position"}</BTN>
        </div>
      </div>
    </div>
  );
}

// PHASE 2: Add Candidates
function CandidatesWizard({ jobId, onClose, onFinish }) {
  const [candidates, setCandidates] = useState([]);
  const [showForm, setShowForm] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    first_name:"", last_name:"", personal_email:"", phone_number:"",
    strengths:"", weaknesses:"", rating:null, evaluation_notes:""
  });

  useEffect(() => { fetchCandidates(); }, []);

  const fetchCandidates = async () => {
    try {
      const res = await axios.get(API + "/preonboarding/" + jobId, { headers: headers() });
      setCandidates(res.data.candidates || []);
    } catch (e) { console.error(e); }
  };

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const inputStyle = { width:"100%", padding:"9px 11px", border:"1px solid #E5E7EB", borderRadius:"6px", fontSize:"13px", color:"#111827", outline:"none", boxSizing:"border-box" };
  const labelStyle = { display:"block", fontSize:"11px", fontWeight:"600", color:"#6B7280", marginBottom:"5px", textTransform:"uppercase", letterSpacing:"0.04em" };

  const saveCandidate = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.personal_email.trim() || !form.phone_number.trim()) {
      setError("Name, email, and phone are required.");
      return;
    }
    setSaving(true); setError("");
    try {
      await axios.post(API + "/preonboarding/" + jobId + "/candidates", form, { headers: headers() });
      setForm({ first_name:"", last_name:"", personal_email:"", phone_number:"", strengths:"", weaknesses:"", rating:null, evaluation_notes:"" });
      fetchCandidates();
      setShowForm(false);
    } catch (e) { setError(e.response?.data?.error || "Failed to add candidate."); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (showForm && (form.first_name.trim() || form.last_name.trim())) {
      await saveCandidate();
    }
  };

  const handleAddAnother = async () => {
    await saveCandidate();
    setShowForm(true);
  };

  const handleFinish = async () => {
    if (candidates.length === 0) { setError("Add at least one candidate before finishing."); return; }
    try {
      await axios.patch(API + "/preonboarding/" + jobId + "/finish", {}, { headers: headers() });
      onFinish();
    } catch (e) { alert("Failed to finish: " + (e.response?.data?.error || e.message)); }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", overflowY:"auto", padding:"20px" }}>
      <div style={{ background:"#fff", borderRadius:"14px", padding:"36px", width:"800px", maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"28px" }}>
          <div>
            <h2 style={{ fontSize:"18px", fontWeight:"700", color:"#111827", margin:0 }}>Add Candidates</h2>
            <p style={{ fontSize:"12px", color:"#9CA3AF", margin:"4px 0 0 0" }}>Phase 2: Enter candidate information</p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", fontSize:"22px", lineHeight:1 }}>×</button>
        </div>

        {error && <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:"6px", padding:"10px 14px", marginBottom:"18px", fontSize:"12px", color:"#B91C1C" }}>{error}</div>}

        {candidates.length > 0 && (
          <div style={{ marginBottom:"20px" }}>
            <h3 style={{ fontSize:"13px", fontWeight:"600", color:"#374151", marginBottom:"12px" }}>Candidates Added ({candidates.length})</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              {candidates.map(c => (
                <div key={c.id} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"10px 12px", background:"#F9FAFB", borderRadius:"6px", border:"1px solid #E5E7EB" }}>
                  <div style={{ width:"32px", height:"32px", borderRadius:"50%", background: NAVY, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"12px", fontWeight:"700", flexShrink:0 }}>
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:"13px", fontWeight:"600", color:"#111827" }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize:"11px", color:"#6B7280" }}>{c.personal_email} • {c.phone_number}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showForm ? (
          <>
            <h3 style={{ fontSize:"13px", fontWeight:"600", color:"#374151", marginBottom:"12px" }}>{candidates.length > 0 ? "Add Another Candidate" : "Add First Candidate"}</h3>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"20px" }}>
              <div><label style={labelStyle}>First Name *</label><input value={form.first_name} onChange={e => set("first_name", e.target.value)} style={inputStyle}/></div>
              <div><label style={labelStyle}>Last Name *</label><input value={form.last_name} onChange={e => set("last_name", e.target.value)} style={inputStyle}/></div>
              <div><label style={labelStyle}>Personal Email *</label><input type="email" value={form.personal_email} onChange={e => set("personal_email", e.target.value)} style={inputStyle}/></div>
              <div><label style={labelStyle}>Phone Number *</label><input value={form.phone_number} onChange={e => set("phone_number", e.target.value)} style={inputStyle}/></div>
              <div style={{ gridColumn:"1 / -1" }}><label style={labelStyle}>Strengths</label><textarea value={form.strengths} onChange={e => set("strengths", e.target.value)} style={{...inputStyle, minHeight:"60px", resize:"vertical"}}/></div>
              <div style={{ gridColumn:"1 / -1" }}><label style={labelStyle}>Weaknesses</label><textarea value={form.weaknesses} onChange={e => set("weaknesses", e.target.value)} style={{...inputStyle, minHeight:"60px", resize:"vertical"}}/></div>
              <div>
                <label style={labelStyle}>Rating</label>
                <div style={{ display:"flex", gap:"8px" }}>
                  {[1,2,3,4,5].map(r => (
                    <button key={r} onClick={() => set("rating", r)} style={{ background: form.rating === r ? GOLD : "#E5E7EB", border:"none", borderRadius:"6px", padding:"8px 14px", cursor:"pointer", fontSize:"14px", fontWeight:"600", color: form.rating === r ? "#fff" : "#6B7280" }}>
                      {r} ★
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ gridColumn:"1 / -1" }}><label style={labelStyle}>Evaluation Notes</label><textarea value={form.evaluation_notes} onChange={e => set("evaluation_notes", e.target.value)} style={{...inputStyle, minHeight:"60px", resize:"vertical"}}/></div>
            </div>
          </>
        ) : (
          <div style={{ textAlign:"center", padding:"20px" }}>
            <BTN variant="primary" onClick={() => setShowForm(true)}>+ Add Another Candidate</BTN>
          </div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", gap:"8px" }}>
          <BTN onClick={onClose}>Cancel</BTN>
          {showForm && <BTN variant="default" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</BTN>}
          {showForm && <BTN variant="primary" onClick={handleAddAnother} disabled={saving}>{saving ? "Saving..." : "Add Another Candidate"}</BTN>}
          <BTN variant="orange" onClick={handleFinish} disabled={candidates.length === 0}>Finish</BTN>
        </div>
      </div>
    </div>
  );
}

// Edit Candidate Modal
function EditCandidateModal({ candidate, jobId, onClose, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    first_name: candidate.first_name || "",
    last_name: candidate.last_name || "",
    personal_email: candidate.personal_email || "",
    phone_number: candidate.phone_number || "",
    strengths: candidate.strengths || "",
    weaknesses: candidate.weaknesses || "",
    rating: candidate.rating || null,
    evaluation_notes: candidate.evaluation_notes || ""
  });

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const inputStyle = { width:"100%", padding:"9px 11px", border:"1px solid #E5E7EB", borderRadius:"6px", fontSize:"13px", color:"#111827", outline:"none", boxSizing:"border-box" };
  const labelStyle = { display:"block", fontSize:"11px", fontWeight:"600", color:"#6B7280", marginBottom:"5px", textTransform:"uppercase", letterSpacing:"0.04em" };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await axios.patch(API + "/preonboarding/" + jobId + "/candidates/" + candidate.id, form, { headers: headers() });
      onUpdate();
      onClose();
    } catch (e) { setError(e.response?.data?.error || "Failed to update candidate."); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1001, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:"10px", padding:"24px", width:"600px", maxWidth:"90vw", maxHeight:"80vh", overflowY:"auto" }}>
        <h3 style={{ fontSize:"16px", fontWeight:"700", color:"#111827", marginBottom:"16px" }}>Edit Candidate: {candidate.first_name} {candidate.last_name}</h3>
        {error && <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:"6px", padding:"10px 14px", marginBottom:"12px", fontSize:"12px", color:"#B91C1C" }}>{error}</div>}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
          <div><label style={labelStyle}>First Name *</label><input value={form.first_name} onChange={e => set("first_name", e.target.value)} style={inputStyle}/></div>
          <div><label style={labelStyle}>Last Name *</label><input value={form.last_name} onChange={e => set("last_name", e.target.value)} style={inputStyle}/></div>
          <div><label style={labelStyle}>Personal Email *</label><input type="email" value={form.personal_email} onChange={e => set("personal_email", e.target.value)} style={inputStyle}/></div>
          <div><label style={labelStyle}>Phone Number *</label><input value={form.phone_number} onChange={e => set("phone_number", e.target.value)} style={inputStyle}/></div>
          <div style={{ gridColumn:"1 / -1" }}><label style={labelStyle}>Strengths</label><textarea value={form.strengths} onChange={e => set("strengths", e.target.value)} style={{...inputStyle, minHeight:"60px", resize:"vertical"}}/></div>
          <div style={{ gridColumn:"1 / -1" }}><label style={labelStyle}>Weaknesses</label><textarea value={form.weaknesses} onChange={e => set("weaknesses", e.target.value)} style={{...inputStyle, minHeight:"60px", resize:"vertical"}}/></div>
          <div>
            <label style={labelStyle}>Rating</label>
            <div style={{ display:"flex", gap:"6px" }}>
              {[1,2,3,4,5].map(r => (
                <button key={r} onClick={() => set("rating", r)} style={{ background: form.rating === r ? GOLD : "#E5E7EB", border:"none", borderRadius:"6px", padding:"8px 12px", cursor:"pointer", fontSize:"13px", fontWeight:"600", color: form.rating === r ? "#fff" : "#6B7280" }}>
                  {r} ★
                </button>
              ))}
            </div>
          </div>
          <div style={{ gridColumn:"1 / -1" }}><label style={labelStyle}>Evaluation Notes</label><textarea value={form.evaluation_notes} onChange={e => set("evaluation_notes", e.target.value)} style={{...inputStyle, minHeight:"60px", resize:"vertical"}}/></div>
        </div>
        <div style={{ display:"flex", gap:"8px", justifyContent:"flex-end", marginTop:"16px" }}>
          <BTN onClick={onClose}>Cancel</BTN>
          <BTN variant="primary" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</BTN>
        </div>
      </div>
    </div>
  );
}

// Job Detail Modal
function JobDetailModal({ job, onClose, onUpdate }) {
  const [winnerId, setWinnerId] = useState(null);
  const [awarding, setAwarding] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [marking, setMarking] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);

  const handleMarkReady = async () => {
    setMarking(true);
    try {
      await axios.patch(API + "/preonboarding/" + job.id + "/mark-ready", {}, { headers: headers() });
      onUpdate();
      onClose();
    } catch (e) { alert(e.response?.data?.error || "Failed to mark ready"); }
    setMarking(false);
  };

  const handleAward = async () => {
    if (!winnerId) return;
    setAwarding(true);
    try {
      await axios.patch(API + "/preonboarding/" + job.id + "/award", { candidate_id: winnerId }, { headers: headers() });
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
        onUpdate();
        onClose();
      }, 3000);
    } catch (e) {
      alert(e.response?.data?.error || "Failed to award position");
      setAwarding(false);
    }
  };

  const isPending = job.status === 'pending';
  const isOpen = job.status === 'open';
  const candidates = job.candidates || [];

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      {showConfetti && <Confetti />}
      {editingCandidate && <EditCandidateModal candidate={editingCandidate} jobId={job.id} onClose={() => setEditingCandidate(null)} onUpdate={onUpdate} />}
      <div style={{ background:"#fff", borderRadius:"14px", padding:"36px", width:"660px", maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"28px" }}>
          <div>
            <h2 style={{ fontSize:"18px", fontWeight:"700", color:"#111827", margin:0 }}>{job.job_title}</h2>
            <p style={{ fontSize:"12px", color:"#9CA3AF", margin:"4px 0 0 0" }}>
              {job.department}{job.job_classification ? " - " + job.job_classification : ""}
            </p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", fontSize:"22px", lineHeight:1 }}>×</button>
        </div>

        <div style={{ marginBottom:"20px" }}>
          <h3 style={{ fontSize:"13px", fontWeight:"600", color:"#374151", marginBottom:"12px" }}>
            {isPending ? "Select candidate to award position:" : "Candidates"}
          </h3>
          
          {candidates.length > 0 ? (
            candidates.map(c => (
              <div
                key={c.id}
                onClick={() => {
                  if (isPending) setWinnerId(c.id);
                  else if (isOpen) setEditingCandidate(c);
                }}
                style={{
                  display:"flex",
                  alignItems:"center",
                  gap:"14px",
                  padding:"14px 16px",
                  borderRadius:"8px",
                  border: isPending ? "2px solid " + (winnerId === c.id ? NAVY : "#E5E7EB") : "1px solid #E5E7EB",
                  background: isPending && winnerId === c.id ? "#F0F4FF" : "#fff",
                  cursor: isPending || isOpen ? "pointer" : "default",
                  marginBottom:"10px",
                  transition:"all 0.15s"
                }}
              >
                {isPending && (
                  <div style={{
                    width:"20px",
                    height:"20px",
                    borderRadius:"50%",
                    border:"2px solid " + (winnerId === c.id ? NAVY : "#D1D5DB"),
                    background: winnerId === c.id ? NAVY : "#fff",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"center",
                    flexShrink:0
                  }}>
                    {winnerId === c.id && <div style={{ width:"8px", height:"8px", borderRadius:"50%", background:"#fff" }}/>}
                  </div>
                )}
                <div style={{ width:"44px", height:"44px", borderRadius:"50%", background: NAVY, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"14px", fontWeight:"700", flexShrink:0 }}>
                  {c.first_name?.[0]}{c.last_name?.[0]}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:"14px", fontWeight:"600", color:"#111827" }}>{c.first_name} {c.last_name}</div>
                  <div style={{ fontSize:"12px", color:"#6B7280" }}>{c.personal_email} • {c.phone_number}</div>
                  {c.rating && <div style={{ fontSize:"11px", color:GOLD, marginTop:"2px" }}>{"★".repeat(c.rating)}</div>}
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign:"center", padding:"40px", color:"#9CA3AF", border:"2px dashed #E5E7EB", borderRadius:"8px" }}>
              No candidates added yet.
            </div>
          )}
        </div>

        <div style={{ display:"flex", justifyContent:"flex-end", gap:"8px", marginTop:"28px" }}>
          <BTN onClick={onClose}>Cancel</BTN>
          {isOpen && (
            <BTN variant="orange" onClick={handleMarkReady} disabled={marking || candidates.length === 0}>
              {marking ? "Marking..." : "Interviews Complete - Ready to Award"}
            </BTN>
          )}
          {isPending && (
            <BTN variant="orange" onClick={handleAward} disabled={!winnerId || awarding}>
              {awarding ? "Awarding..." : "Award Position"}
            </BTN>
          )}
        </div>
      </div>
    </div>
  );
}

// Awarded Job Card with Congratulations Letter button
function AwardedJobCard({ job, isAdmin, onAdvance, advancing, onRefresh }) {
  const winner = job.candidates?.find(c => c.id === job.awarded_candidate_id);
  const finalists = job.candidates?.filter(c => c.id !== job.awarded_candidate_id) || [];
  const [showLetterModal, setShowLetterModal] = useState(false);

  return (
    <div style={{ border:"2px solid " + GOLD, borderRadius:"12px", overflow:"hidden", background:"#fff", boxShadow:"0 4px 24px rgba(245,158,11,0.15)" }}>
      {showLetterModal && <CongratulationsLetterModal job={job} candidate={winner} onClose={() => setShowLetterModal(false)} onSent={onRefresh} />}
      
      <div style={{ background:"linear-gradient(135deg, " + NAVY + ", #2d5a8e)", padding:"12px 18px", display:"flex", alignItems:"center", gap:"10px" }}>
        <span style={{ fontSize:"20px" }}>🌟</span>
        <div>
          <div style={{ fontSize:"14px", fontWeight:"700", color:"#fff" }}>{job.job_title}</div>
          <div style={{ fontSize:"11px", color:"#BFD8F0" }}>{job.department}{job.job_classification ? " - " + job.job_classification : ""}</div>
        </div>
        <div style={{ marginLeft:"auto", textAlign:"right" }}>
          <div style={{ fontSize:"10px", color:"#BFD8F0" }}>Awarded</div>
          <div style={{ fontSize:"11px", fontWeight:"600", color:"#FDE68A" }}>{new Date(job.awarded_at).toLocaleDateString()}</div>
        </div>
      </div>

      {winner && (
        <div style={{ padding:"16px 18px", borderBottom: finalists.length > 0 ? "1px solid #FDE68A" : "none" }}>
          <div style={{ fontSize:"10px", fontWeight:"700", color:GOLD, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"8px" }}>Position Awarded To</div>
          <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
            <div style={{ width:"44px", height:"44px", borderRadius:"50%", background:"linear-gradient(135deg, " + GOLD + ", #D97706)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"16px", fontWeight:"700", flexShrink:0 }}>
              {winner.first_name?.[0]}{winner.last_name?.[0]}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:"15px", fontWeight:"700", color:"#111827" }}>{winner.first_name} {winner.last_name}</div>
              {isAdmin && winner.personal_email && <div style={{ fontSize:"12px", color:"#6B7280" }}>{winner.personal_email}</div>}
            </div>
          </div>
        </div>
      )}

      {finalists.length > 0 && (
        <div style={{ padding:"12px 18px", borderBottom:"1px solid #F3F4F6" }}>
          <div style={{ fontSize:"10px", fontWeight:"700", color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"8px" }}>Finalists</div>
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
            {finalists.map(f => (
              <div key={f.id} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"6px 10px", background:"#F9FAFB", borderRadius:"8px", border:"1px solid #E5E7EB" }}>
                <div style={{ width:"28px", height:"28px", borderRadius:"50%", background:NAVY, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"10px", fontWeight:"700", flexShrink:0 }}>
                  {f.first_name?.[0]}{f.last_name?.[0]}
                </div>
                <div>
                  <div style={{ fontSize:"12px", fontWeight:"600", color:"#111827" }}>{f.first_name} {f.last_name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!job.onboarding_case_id && (
        <div style={{ padding:"14px 18px", background:"#FFFBEB" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
            <BTN variant="primary" onClick={() => setShowLetterModal(true)} style={{ width:"100%" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              Send Congratulations Letter
            </BTN>
            <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
              <div style={{ flex:1, height:"1px", background:"#E5E7EB" }}/>
              <span style={{ fontSize:"11px", color:"#9CA3AF", fontWeight:"600" }}>THEN</span>
              <div style={{ flex:1, height:"1px", background:"#E5E7EB" }}/>
            </div>
            <BTN variant="orange" onClick={onAdvance} disabled={advancing} style={{ width:"100%" }}>
              {advancing ? "Advancing..." : "Advance to Onboarding"}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </BTN>
          </div>
        </div>
      )}

      {job.onboarding_case_id && (
        <div style={{ padding:"12px 18px", background:"#ECFDF5", display:"flex", alignItems:"center", gap:"8px" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          <span style={{ fontSize:"12px", color:"#065F46", fontWeight:"600" }}>Onboarding in progress — Case #{job.onboarding_case_id}</span>
        </div>
      )}
    </div>
  );
}

function JobCard({ job, onClick }) {
  const statusColor = job.status === 'pending' ? '#F59E0B' : job.status === 'draft' ? '#9CA3AF' : '#3B82F6';
  const statusText = job.status === 'pending' ? 'Ready to Award' : job.status === 'draft' ? 'Draft' : 'Open';
  
  return (
    <div onClick={onClick} style={{ border:"1px solid #E5E7EB", borderRadius:"10px", padding:"18px", background:"#fff", cursor:"pointer", transition:"all 0.15s" }}
         onMouseOver={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"}
         onMouseOut={e => e.currentTarget.style.boxShadow = "none"}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"10px" }}>
        <div>
          <div style={{ fontSize:"14px", fontWeight:"700", color:"#111827" }}>{job.job_title}</div>
          {job.department && <div style={{ fontSize:"11px", color:"#6B7280", marginTop:"2px" }}>{job.department}</div>}
        </div>
        <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", padding:"3px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"600", background: job.status === 'pending' ? '#FEF3C7' : job.status === 'draft' ? '#F3F4F6' : '#EFF6FF', color: job.status === 'pending' ? '#92400E' : job.status === 'draft' ? '#6B7280' : '#1E40AF' }}>
          <span style={{ width:"5px", height:"5px", borderRadius:"50%", background: statusColor }}/>
          {statusText}
        </span>
      </div>
      {job.status === 'draft' && (
        <div style={{ marginTop:"12px" }}>
          <BTN variant="orange" style={{ fontSize:"12px", padding:"6px 12px" }}>Award Position</BTN>
        </div>
      )}
    </div>
  );
}

export default function PreOnboarding({ navigateTo, isAdmin }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobWizard, setShowJobWizard] = useState(false);
  const [showCandidatesWizard, setShowCandidatesWizard] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [toast, setToast] = useState(null);
  const [advancing, setAdvancing] = useState(null);

  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + "/preonboarding", { headers: headers() });
      setJobs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleJobSave = () => {
    setShowJobWizard(false);
    fetchJobs();
    setToast("Job saved successfully!");
  };

  const handleAdvanceToCandidates = (jobId) => {
    setShowJobWizard(false);
    setShowCandidatesWizard(jobId);
    fetchJobs();
  };

  const handleCandidatesFinish = () => {
    setShowCandidatesWizard(null);
    fetchJobs();
    setToast("Job opened successfully!");
  };

  const handleAdvance = async (jobId) => {
    setAdvancing(jobId);
    try {
      const res = await axios.patch(API + "/preonboarding/" + jobId + "/advance", {}, { headers: headers() });
      fetchJobs();
      setToast("Onboarding initiated! IT and HR have been notified.");
      if (navigateTo && res.data.onboarding_case_id) {
        setTimeout(() => navigateTo("onboarding", res.data.onboarding_case_id), 2500);
      }
    } catch (e) { alert(e.response?.data?.error || "Failed to advance."); }
    finally { setAdvancing(null); }
  };

  const handleJobClick = (job) => {
    if (job.status === 'draft') {
      setShowCandidatesWizard(job.id);
    } else {
      setSelectedJob(job);
    }
  };

  const draft    = jobs.filter(j => j.status === "draft");
  const open     = jobs.filter(j => j.status === "open" || j.status === "pending");
  const awarded  = jobs.filter(j => j.status === "awarded");

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"300px", gap:"10px" }}>
      <div style={{ width:"18px", height:"18px", border:"2px solid #E0E7FF", borderTopColor: NAVY, borderRadius:"50%", animation:"spin 0.7s linear infinite" }}/>
      <span style={{ color:"#6B7280", fontSize:"13px" }}>Loading...</span>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );

  return (
    <div style={{ padding:"24px 28px" }}>
      {showJobWizard && <JobDetailsWizard onClose={() => setShowJobWizard(false)} onSave={handleJobSave} onAdvanceToCandidates={handleAdvanceToCandidates} />}
      {showCandidatesWizard && <CandidatesWizard jobId={showCandidatesWizard} onClose={() => setShowCandidatesWizard(null)} onFinish={handleCandidatesFinish} />}
      {selectedJob && <JobDetailModal job={selectedJob} onClose={() => setSelectedJob(null)} onUpdate={fetchJobs} />}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px" }}>
        <div>
          <h2 style={{ fontSize:"16px", fontWeight:"700", color:"#111827", margin:"0 0 2px 0" }}>Pre-Onboarding</h2>
          <p style={{ fontSize:"12px", color:"#9CA3AF", margin:0 }}>{draft.length} draft - {open.length} open - {awarded.length} awarded</p>
        </div>
        <BTN variant="orange" onClick={() => setShowJobWizard(true)}>+ New Job Opening</BTN>
      </div>

      {jobs.length === 0 ? (
        <div style={{ textAlign:"center", padding:"80px", color:"#9CA3AF" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ marginBottom:"16px" }}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
          <p style={{ fontSize:"14px", fontWeight:"600", color:"#6B7280", marginBottom:"8px" }}>No job openings yet</p>
          <p style={{ fontSize:"13px", marginBottom:"20px" }}>Click New Job Opening to start the hiring process</p>
          <BTN variant="orange" onClick={() => setShowJobWizard(true)}>New Job Opening</BTN>
        </div>
      ) : (
        <>
          {awarded.length > 0 && (
            <>
              <h3 style={{ fontSize:"12px", fontWeight:"700", color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"12px" }}>Awarded ({awarded.length})</h3>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(400px, 1fr))", gap:"16px", marginBottom:"28px" }}>
                {awarded.map(j => (
                  <AwardedJobCard
                    key={j.id}
                    job={j}
                    isAdmin={isAdmin}
                    onAdvance={() => handleAdvance(j.id)}
                    advancing={advancing === j.id}
                    onRefresh={fetchJobs}
                  />
                ))}
              </div>
            </>
          )}
          {open.length > 0 && (
            <>
              <h3 style={{ fontSize:"12px", fontWeight:"700", color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"12px" }}>Open ({open.length})</h3>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:"12px", marginBottom:"28px" }}>
                {open.map(j => <JobCard key={j.id} job={j} onClick={() => handleJobClick(j)} />)}
              </div>
            </>
          )}
          {draft.length > 0 && (
            <>
              <h3 style={{ fontSize:"12px", fontWeight:"700", color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"12px" }}>Draft ({draft.length})</h3>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:"12px" }}>
                {draft.map(j => <JobCard key={j.id} job={j} onClick={() => handleJobClick(j)} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
